import { useTranslation } from "react-i18next";
import UserActionPagesTemplate from "../../Template/UserActionPagesTemplate";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { SET_MESSAGE } from "../../Actions/Types";
import { resetPasswordService } from "../../Services/UserAction/ResetPasswordService";
import { isValidPassword } from "../../Helpers/isValidPassword";
import aioaLogo from "../../Assets/img/all-in-one-accessibility-logo.svg";

const ResetPasswordPage = () => {
  const { user } = useSelector((store: StoreProptypes) => store);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get("token");
  const ResetEmail = searchParams.get("email");
  const is2FAEnabledParam = searchParams.get("is_google2fa_enabled");
  const is2FAEnabled = Number(is2FAEnabledParam);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [userEmail, setUserEmail] = useState("");
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [cpassword, setCPassword] = useState("");
  const [otp, setOtp] = useState("");

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t(
            "You're already logged In. You can change your password via profile settings."
          ),
          toast_type: "success",
        },
      });
      navigate("/front/edit-profile/");
    } else {
      resetToken && setToken(resetToken);
      ResetEmail && setUserEmail(ResetEmail);
    }
  }, [resetToken, ResetEmail]);

  // Focus input field
  const handleFocus = () => {
    if (inputRef && inputRef.current) inputRef.current.focus();
  };

  // Form submission
  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const trimmedOtp = otp.trim();
    const isValidOTP = /^\d{6}$/.test(trimmedOtp);
    const isValidRecovery = /^[a-zA-Z0-9]{8,24}$/.test(trimmedOtp);

    if (!password) {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("Please enter your password."),
          toast_type: "danger",
        },
      });
      return;
    }

    if (!isValidPassword(password)) {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("password-criteria"),
          toast_type: "danger",
        },
      });
      return;
    }

    if (password !== cpassword) {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("Password and confirm password doesn't match."),
          toast_type: "danger",
        },
      });
      return;
    }

    // If 2FA is required, check for OTP or recovery code
    if (is2FAEnabled === 1 && !isValidOTP && !isValidRecovery) {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t(
            "Please enter a valid 6-digit OTP or recovery code."
          ),
          toast_type: "danger",
        },
      });
      return;
    }

    // Call API to reset password
    if (userEmail && token && password && cpassword) {
      resetPasswordService(
        userEmail,
        token,
        password,
        cpassword,
        handleFocus,
        t,
        dispatch,
        navigate,
        trimmedOtp
      );
    }
  };

  return (
    <UserActionPagesTemplate>
      <div className="aioa_dashboard-header-domain-wrapper mb-2 w-75 m-auto mt-4">
         <img src={aioaLogo} alt="All in One Accessibility Logo" />
      </div>
      <h1 className="mt-5">{t("Reset Password")}</h1>
      <Form>
        {/* Password Field */}
        <Form.Group className="form-group" controlId="user_email">
          <Form.Label>{t("New Password")}</Form.Label>
          <Form.Control
            type="password"
            aria-label="New Password"
            onChange={(e) => setPassword(e.target.value)}
            value={password}
            ref={inputRef}
            autoComplete="new-password"
            aria-describedby="passwordHelpBlock"
            size="lg"
          />
          <Form.Text id="passwordHelpBlock" muted>
            {t("password-criteria")}
          </Form.Text>
        </Form.Group>

        {/* Confirm Password Field */}
        <Form.Group className="form-group" controlId="confirm_password">
          <Form.Label>{t("Confirm Password")}</Form.Label>
          <Form.Control
            type="password"
            aria-label="Confirm Password"
            onChange={(e) => setCPassword(e.target.value)}
            value={cpassword}
            autoComplete="new-password"
            size="lg"
          />
          <Form.Text muted>{t("Re-enter password to confirm")}</Form.Text>
        </Form.Group>

        {/* OTP / Recovery Code Field */}
        {is2FAEnabled === 1 && (
          <Form.Group className="form-group" controlId="otp">
            <Form.Label>
              {t("Two factor authentication code or recovery code")}
            </Form.Label>
            <Form.Control
              type="text"
              aria-label="OTP"
              placeholder={t("enter 6-digit OTP or recovery code")}
              onChange={(e) => setOtp(e.target.value)}
              value={otp}
              maxLength={24}
              size="lg"
            />
            <Form.Text muted>
              {t(
                "You can use your authenticator code or one of your recovery codes."
              )}
            </Form.Text>
          </Form.Group>
        )}

        {/* Submit Button */}
        <Button
          variant="primary"
          type="submit"
          size="lg"
          className="w-100 submit-btn"
          onClick={handleSubmit}
        >
          {t("Reset Password")}
        </Button>
      </Form>

      {/* Link to Login */}
      <div className="aioa_dashboard-login-link">
        <Link to="/front/login" className="aioa_free-trial-link">
          {t("Login")}
        </Link>
      </div>
    </UserActionPagesTemplate>
  );
};

export default ResetPasswordPage;
